import { FieldContainer, Modal, Table } from '@odin-labs/components';
import { useGetBadgeConnectionsQuery } from 'apollo/generated/client-operations';
import { LoadingError } from 'components/loadingError';
import { getConnectionColumns } from 'connectors/components';
import { WorkerCard } from 'graphql/server/queries/types';
import React from 'react';
import { getPrettyFormattedDateTime } from 'utils/dates';
import { getWorkerCardStatusConfig } from './WorkerBadges.tables';

function BadgeField({
  name,
  label,
  children,
}: {
  name: string;
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <FieldContainer
      name={name}
      label={label}
      className="odin-col-span-6 sm:odin-col-span-3"
    >
      <p className="odin-text-sm odin-text-gray-900">{children}</p>
    </FieldContainer>
  );
}

export function BadgeDetailsDrawer({
  badge,
  onClose,
}: {
  badge: WorkerCard | null;
  onClose: () => void;
}): React.ReactElement {
  const { data, loading, error } = useGetBadgeConnectionsQuery({
    variables: { badgeId: badge?.workerCardId ?? '' },
    fetchPolicy: 'no-cache',
    skip: !badge,
  });
  const connections = data?.getWorkerCard.connections ?? [];
  const columns = React.useMemo(() => getConnectionColumns(), []);
  let title = 'Badge details';
  if (badge) title = `Badge ${badge.cardNumber}`;

  return (
    <Modal
      open={Boolean(badge)}
      setOpen={(open) => !open && onClose()}
      variant="drawer"
      size="md"
      title={title}
      actionsPanel={null}
    >
      {badge && (
        <div className="odin-space-y-10">
          <div className="odin-grid odin-grid-cols-6 odin-gap-6">
            <BadgeField name="badgeNumber" label="Badge number">
              {badge.cardNumber}
            </BadgeField>
            <BadgeField name="status" label="Status">
              {getWorkerCardStatusConfig(badge)?.text ?? 'Unknown'}
            </BadgeField>
            <BadgeField name="authorization" label="Authorization">
              {badge.isTemporary ? 'Temporary' : 'Permanent'}
            </BadgeField>
            <BadgeField name="badgeType" label="Badge type">
              {badge.workerCardFormat?.name ?? 'Unknown'}
            </BadgeField>
            <BadgeField name="issuedOn" label="Issued on">
              {getPrettyFormattedDateTime(badge.issuedAt) ?? 'None'}
            </BadgeField>
            <BadgeField name="expiresOn" label="Expires on">
              {getPrettyFormattedDateTime(badge.expiredAt) ?? 'None'}
            </BadgeField>
          </div>

          <section className="odin-space-y-4">
            <h3 className="odin-text-base odin-font-medium odin-text-gray-900">
              Connections
            </h3>
            {error ? (
              <LoadingError error={error} />
            ) : (
              <Table
                columns={columns}
                data={connections}
                loading={loading}
                noResultsText="No connections to show"
                remote
                disableGlobalFilter
                disableSortBy
              />
            )}
          </section>
        </div>
      )}
    </Modal>
  );
}
